// Lib
import React, { useCallback, useEffect } from 'react';
import { Typography, Divider, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Components
import Layout from '../../../components/Layout';
import EmptyCart from '../../../components/Cart/EmptyCart';
import CartBar from '../../../components/Cart/ShippingDetails/CartBar';
import CartCard from '../../../components/Cart/ShippingDetails/CartCard';
import Spinner from '../../../components/Spinner';
import FooterButtons from '../../../components/Cart/ShippingDetails/FooterButtons';
import RatingCard from '../../../components/Cart/RatingCard';
import GridCard from '../../../components/Products/Card/GridCard';
import IconButton from '../../../components/Button/IconButton/IconButton';
import Feather from '../../../components/Feather';
import Error from '../../../components/Error';
import Button from '../../../components/Button';

// Hooks
import useCart from '../hooks/useCart';
import useRequest from '../hooks/useRequest';
import useSaveForLater from '../hooks/useSaveForLater';
import useRatingBeforeNextStep from '../hooks/useRatingBeforeNextStep';
import useUI from '../../../hooks/useUI';
import useOverLimitProduct from '../hooks/useOverLimitProduct';
import useSharedKitsSettings from '../../../hooks/useSharedKitsSettings';
import useSharedKitForm from '../../Kits/hooks/useSharedKitForm';

// Constants
import { ADD_TO_SHARED_KITS_BUTTON } from '../../../constants/sharedKits';

// Actions
import { Open2Modal } from '../../../actions/modal2';
import {
  ClearShippingAddresses,
  ClearShippingDetails,
  GetSaveForLaterProducts,
} from '../../../actions/cart';

// Utils
import { isObjEmpty } from '../../../utils/object';

// Styles
import { useStyles } from './CartAndForLaterStyles';

const DESKTOP_PRODUCT_IMAGE_HEIGHT = 250;
const TABLET_PRODUCT_IMAGE_HEIGHT = 200;
const MOBILE_PRODUCT_IMAGE_HEIGHT = 138;

const CartAndForLater = ({ unavailableProducts = [] }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiError = useSelector((state) => state.apiError?.['getCartProducts']);
  const { classes } = useStyles();
  const {
    isEmptyCart,
    cart,
    totalCountInCart,
    loading,
    productsObj,
    someProductIsMoreThanInStock,
  } = useCart();
  const { productsForRate, onSaveRating, updateProductRating } =
    useRatingBeforeNextStep();
  const {
    isRequestRequired,
    openRequestCard,
    savedRequest,
    onClearOverLimitInDB,
    onSaveOverLimitInDB,
  } = useRequest(null, true);
  const { isSomeProductOverLimit } = useOverLimitProduct();
  const { isTabletMode, isMobileMode } = useUI();
  const { deleteAllFromLater } = useSaveForLater();
  const { canCreate } = useSharedKitsSettings();
  const { onCreateSharedKit } = useSharedKitForm();

  useEffect(() => {
    dispatch(ClearShippingDetails());
    dispatch(ClearShippingAddresses());
    dispatch(GetSaveForLaterProducts());
  }, [dispatch]);

  const renderCartBar = useCallback(
    () => !loading && <CartBar countInCart={totalCountInCart} />,
    [loading, totalCountInCart],
  );

  const goToMaterials = useCallback(() => navigate('/materials'), [navigate]);

  const onNext = useCallback(() => {
    if (isRequestRequired && !cart?.overLimitRequest) {
      openRequestCard();
    } else if (productsForRate.length) {
      dispatch(
        Open2Modal({
          Component: () => (
            <RatingCard
              onSave={onSaveRating}
              updateProductRating={updateProductRating}
            />
          ),
          styles: {
            width: 'min(85vw, 700px)',
          },
          props: {
            paddingContent: {
              xl: '40px 40px 20px',
              lg: '40px 40px 20px',
              md: '40px 12px 20px',
            },
          },
        }),
      );
    } else {
      navigate('/cart/shipping-address');
    }
  }, [
    isRequestRequired,
    dispatch,
    cart,
    navigate,
    productsForRate,
    updateProductRating,
    onSaveRating,
    openRequestCard,
  ]);

  useEffect(() => {
    if (
      !someProductIsMoreThanInStock &&
      isSomeProductOverLimit &&
      !isObjEmpty(productsObj) &&
      !savedRequest &&
      !cart?.shippingAddresses?.length
    ) {
      openRequestCard();
    }
  }, [
    someProductIsMoreThanInStock,
    openRequestCard,
    isSomeProductOverLimit,
    savedRequest,
    productsObj,
    cart?.shippingAddresses?.length,
  ]);

  const renderContent = () => {
    if (loading) {
      return <Spinner />;
    }
    if (apiError) {
      return <Error message={apiError} />;
    }
    return (
      <div className={classes.root}>
        <div className={classes.order}>
          {isEmptyCart ? (
            <EmptyCart />
          ) : (
            <>
              {cart.forOrder?.map(({ product, fromSharedKit }) => (
                <CartCard
                  key={product}
                  product={productsObj[product]}
                  fromSharedKit={fromSharedKit}
                  onClearOverLimitInDB={onClearOverLimitInDB}
                  onSaveOverLimitInDB={onSaveOverLimitInDB}
                />
              ))}
            </>
          )}
          {!!totalCountInCart && (
            <FooterButtons
              nextTitle="Checkout"
              backTitle="Continue Shopping"
              nextButtonDisabled={
                !!unavailableProducts.length || someProductIsMoreThanInStock
              }
              onClickBack={goToMaterials}
              onClickNext={onNext}
              centerSlot={
                canCreate && (
                  <Button
                    id="add-to-shared-kits-button"
                    variant="text"
                    onClick={onCreateSharedKit}
                    data-testid="add-to-shared-kits-button">
                    {ADD_TO_SHARED_KITS_BUTTON}
                  </Button>
                )
              }
            />
          )}
          <Divider className={classes.divider} />
        </div>
        {cart.forLater?.length > 0 ? (
          <div className={classes.saveForLaterContainer}>
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              mb={isMobileMode ? '16px' : '20px'}>
              <Typography variant={isMobileMode ? 'h2' : 'h3'}>
                Saved For Later
              </Typography>
              <IconButton
                startIcon={<Feather type="delete" />}
                onClick={deleteAllFromLater}>
                Remove All
              </IconButton>
            </Box>
            <div className={classes.saveForLaterProducts}>
              {cart.forLater.map((item) => (
                <GridCard
                  key={item.product}
                  product={productsObj[item.product]}
                  isTabletMode={isTabletMode}
                  isMobileMode={isMobileMode}
                  saveForLater={true}
                  nums={item.nums}
                  imageContainer={{
                    desktop: ['100%', DESKTOP_PRODUCT_IMAGE_HEIGHT],
                    tablet: ['100%', TABLET_PRODUCT_IMAGE_HEIGHT],
                    mobile: ['100%', MOBILE_PRODUCT_IMAGE_HEIGHT],
                  }}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className={classes.laterMessage}>
            <Typography color="#929A92" variant="h2" textAlign="center">
              There are no saved for later items.
            </Typography>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout
      className={{ layout: classes.layout }}
      renderComponent={renderCartBar}>
      {renderContent()}
    </Layout>
  );
};

export default CartAndForLater;
